import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '../Tooltip/Tooltip';
import { LAYOUT, PATTERN, SIZE } from './Constants';
import { Label } from './Label';
import styled from 'styled-components';
const BasicLayout = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputLayout__BasicLayout",
  componentId: "sc-xsl4o9-0"
})(["label{display:block;}"]);
BasicLayout.displayName = 'BasicLayout';
const ColumnLayout = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputLayout__ColumnLayout",
  componentId: "sc-xsl4o9-1"
})([""]);
ColumnLayout.displayName = 'ColumnLayout';
const GridLayout = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputLayout__GridLayout",
  componentId: "sc-xsl4o9-2"
})([""]);
GridLayout.displayName = 'GridLayout';
const StyledTooltipError = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputLayout__StyledTooltipError",
  componentId: "sc-xsl4o9-3"
})([""]);
StyledTooltipError.displayName = 'StyledTooltipError';

const getTooltipContextFromStatus = function (touched, valid) {
  if (touched) {
    return valid ? 'valid' : 'invalid';
  }

  return 'info';
};

const wrapChildrenWithTooltip = function (message, children, context, tooltip) {
  const Children = typeof (children === null || children === void 0 ? void 0 : children.type) === 'function' ? children.type(children.props) : children;

  if (tooltip) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      context: context,
      title: message,
      message: tooltip
    }, Children);
  } else {
    return /*#__PURE__*/React.createElement(Tooltip, {
      context: context,
      message: message
    }, Children);
  }
};

const handleValidation = async function (value, props, onValidateCallback) {
  const {
    validationPatterns = []
  } = props; //When flag is required and a patterns.required isn't already in the validationPatterns array, add it.

  if (props.required && validationPatterns.map(_ref => {
    let {
      message
    } = _ref;
    return message;
  }).indexOf(PATTERN.required.message)) {
    validationPatterns.push(PATTERN.required);
  }

  if (typeof onValidateCallback !== 'function' && !validationPatterns.length) {
    return;
  }

  const messages = validationPatterns.map(validator => {
    if (!value.match(validator.pattern)) {
      return /*#__PURE__*/React.createElement(StyledTooltipError, {
        key: validator.message
      }, validator.message);
    } else {
      return false;
    }
  }).filter(message => message);

  try {
    if (typeof onValidateCallback === 'function') {
      const result = await Promise.resolve(onValidateCallback({
        valid: messages.length === 0,
        messages,
        value
      }));

      if (typeof result === 'string') {
        messages.push( /*#__PURE__*/React.createElement(StyledTooltipError, {
          key: result
        }, result));
      } else if (result === false) {
        messages.push( /*#__PURE__*/React.createElement(StyledTooltipError, {
          key: 'onValidate-is-false'
        }));
      }
    }
  } catch (e) {
    // here if we rejected
    messages.push( /*#__PURE__*/React.createElement(StyledTooltipError, {
      key: e.toString()
    }, e.toString()));
  }

  const newState = {};
  newState[props.stateKey + '_touched'] = true;

  if (messages.length === 0) {
    props.onValid(value, props);
    newState[props.stateKey + '_valid'] = true;
    newState[props.stateKey + '_errorMessage'] = null;
  } else {
    newState[props.stateKey + '_valid'] = false;
    newState[props.stateKey + '_errorMessage'] = messages;
  }

  props.boundSource.setState(newState);
};

const getLayoutComponent = function (layout, props) {
  var _props$children, _props$boundSource, _props$boundSource2, _props$boundSource3;

  if (!(props !== null && props !== void 0 && (_props$children = props.children) !== null && _props$children !== void 0 && _props$children.type)) {
    switch (layout) {
      case LAYOUT.basic:
        return /*#__PURE__*/React.createElement(BasicLayout, null);

      case LAYOUT.column:
        return /*#__PURE__*/React.createElement(ColumnLayout, null);

      case LAYOUT.grid:
        return /*#__PURE__*/React.createElement(GridLayout, null);
    }
  }

  const Child = props.children.type;
  const touched = (props === null || props === void 0 ? void 0 : (_props$boundSource = props.boundSource) === null || _props$boundSource === void 0 ? void 0 : _props$boundSource.state[props.stateKey + '_touched']) || props.touched || false;
  const valid = (props === null || props === void 0 ? void 0 : (_props$boundSource2 = props.boundSource) === null || _props$boundSource2 === void 0 ? void 0 : _props$boundSource2.state[props.stateKey + '_valid']) || props.valid || false;
  const tooltipMessage = (props === null || props === void 0 ? void 0 : (_props$boundSource3 = props.boundSource) === null || _props$boundSource3 === void 0 ? void 0 : _props$boundSource3.state[props.stateKey + '_errorMessage']) || props.tooltip || null;

  const wrappedValidate = (v, p) => handleValidation(v, p, props.children.props.onValidate);

  let _children;

  if (touched && !valid) {
    _children = wrapChildrenWithTooltip(props.label, /*#__PURE__*/React.createElement(Child, _extends({}, props.children.props, {
      boundSource: props.boundSource,
      size: props.size,
      onValidate: wrappedValidate,
      touched: touched,
      valid: valid
    })), getTooltipContextFromStatus(touched, valid), tooltipMessage);
  } else {
    _children = /*#__PURE__*/React.createElement(Child, _extends({}, props.children.props, {
      boundSource: props.boundSource,
      size: props.size,
      onValidate: wrappedValidate,
      touched: touched,
      valid: valid
    }));
  }

  switch (layout) {
    case LAYOUT.basic:
      return /*#__PURE__*/React.createElement(BasicLayout, null, /*#__PURE__*/React.createElement(Label, {
        size: props.size,
        required: props.required,
        touched: touched,
        valid: valid
      }, props.label), _children);

    case LAYOUT.column:
      return /*#__PURE__*/React.createElement(ColumnLayout, null, /*#__PURE__*/React.createElement(Label, {
        size: props.size,
        required: props.required
      }, props.label), _children);

    case LAYOUT.grid:
      return /*#__PURE__*/React.createElement(GridLayout, null, wrapChildrenWithTooltip(props.label, /*#__PURE__*/React.createElement(Child, _extends({
        boundSource: props.boundSource
      }, props.children.props, {
        size: props.size,
        layout: "grid",
        placeholder: props.label,
        onValidate: wrappedValidate,
        touched: touched,
        valid: valid
      })), getTooltipContextFromStatus(touched, valid), tooltipMessage));
  }
};

export const InputLayout = function (props) {
  return getLayoutComponent(props.layout, props);
};
InputLayout.propTypes = {
  layout: PropTypes.oneOf([LAYOUT.basic, LAYOUT.column, LAYOUT.grid]),
  size: PropTypes.oneOf([SIZE.small, SIZE.medium, SIZE.large]),
  required: PropTypes.bool,
  touched: PropTypes.bool,
  valid: PropTypes.bool,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  children: PropTypes.node
};
InputLayout.defaultProps = {
  layout: LAYOUT.basic,
  size: SIZE.medium
};